import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { ChevronDown, ChevronRight, Edit3, Trash } from "lucide-react";
import WorkOrderTemplateModal from "../components/WorkOrderTemplateModal";
import WorkOrderTaskModal from "../components/WorkOrderTaskModal";
import {
  fetchWorkOrderTemplates,
  createWorkOrderTemplate,
  updateWorkOrderTemplate,
  deleteWorkOrderTemplate,
  deleteWorkOrderTask,
  createWorkOrderTask,
  updateWorkOrderTask,
  reorderWorkOrderTasks,
} from "../api/WorkOrderTemplate";
import Footer from "../components/Footer";

function WorkOrderManagement() {
  const [workOrderTemplates, setWorkOrderTemplates] = useState([]);
  const [expandedTemplates, setExpandedTemplates] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch templates on mount
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templates = await fetchWorkOrderTemplates();
        setWorkOrderTemplates(templates);
      } catch (error) {
        console.error("Error fetching work order templates:", error);
        setError("Failed to load work order templates. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, []);

  // Save template (create or update)
  const saveTemplate = async (template) => {
    try {
      if (template.workOrderTemplateID) {
        const updatedTemplate = await updateWorkOrderTemplate(
          template.workOrderTemplateID,
          template
        );
        setWorkOrderTemplates((prev) =>
          prev.map((t) =>
            t.workOrderTemplateID === updatedTemplate.workOrderTemplateID
              ? updatedTemplate
              : t
          )
        );
      } else {
        const newTemplate = await createWorkOrderTemplate(template);
        setWorkOrderTemplates((prev) => [...prev, newTemplate]);
      }
    } catch (error) {
      console.error("Error saving template:", error);
      setError("Failed to save template. Please try again.");
    } finally {
      setIsModalOpen(false);
    }
  };

  // Delete template
  const deleteTemplate = async (templateId) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      try {
        await deleteWorkOrderTemplate(templateId);
        setWorkOrderTemplates((prev) =>
          prev.filter((template) => template.workOrderTemplateID !== templateId)
        );
      } catch (error) {
        console.error("Error deleting template:", error);
        setError("Failed to delete template. Please try again.");
      }
    }
  };

  // Save task (create or update)
  const saveTask = async (task) => {
    try {
      if (!currentTemplate?.workOrderTemplateID) {
        throw new Error("Template ID is missing.");
      }

      if (task.id) {
        const updatedTask = await updateWorkOrderTask(
          currentTemplate.workOrderTemplateID,
          task.id,
          task
        );
        setWorkOrderTemplates((prev) =>
          prev.map((template) =>
            template.workOrderTemplateID === currentTemplate.workOrderTemplateID
              ? {
                  ...template,
                  workordertasks: template.workordertasks.map((t) =>
                    t.id === updatedTask.id ? updatedTask : t
                  ),
                }
              : template
          )
        );
      } else {
        const newTask = await createWorkOrderTask(
          currentTemplate.workOrderTemplateID,
          task
        );
        setWorkOrderTemplates((prev) =>
          prev.map((template) =>
            template.workOrderTemplateID === currentTemplate.workOrderTemplateID
              ? {
                  ...template,
                  workordertasks: [...(template.workordertasks || []), newTask],
                }
              : template
          )
        );
      }
    } catch (error) {
      console.error("Error saving task:", error);
      setError("Failed to save the task. Please try again.");
    } finally {
      setIsTaskModalOpen(false);
    }
  };

  // Delete task
  const deleteTask = async (taskId, templateId) => {
    if (window.confirm("Are you sure you want to delete this task?")) {
      try {
        await deleteWorkOrderTask(templateId, taskId);
        setWorkOrderTemplates((prev) =>
          prev.map((template) =>
            template.workOrderTemplateID === templateId
              ? {
                  ...template,
                  workordertasks: template.workordertasks.filter(
                    (task) => task.id !== taskId
                  ),
                }
              : template
          )
        );
      } catch (error) {
        console.error("Error deleting task:", error);
        setError("Failed to delete the task. Please try again.");
      }
    }
  };

  // Handle task reordering
  const handleDragEnd = async (result, templateId) => {
    if (!result.destination) return;

    const template = workOrderTemplates.find(
      (t) => t.workOrderTemplateID === templateId
    );
    const reorderedTasks = Array.from(template.workordertasks || []);
    const [movedTask] = reorderedTasks.splice(result.source.index, 1);
    reorderedTasks.splice(result.destination.index, 0, movedTask);

    try {
      await reorderWorkOrderTasks(templateId, reorderedTasks);
      setWorkOrderTemplates((prev) =>
        prev.map((t) =>
          t.workOrderTemplateID === templateId
            ? { ...t, workordertasks: reorderedTasks }
            : t
        )
      );
    } catch (error) {
      console.error("Error reordering tasks:", error);
      setError("Failed to reorder tasks. Please try again.");
    }
  };

  // Toggle template expand/collapse
  const toggleTemplate = (templateId) => {
    setExpandedTemplates((prev) => ({
      ...prev,
      [templateId]: !prev[templateId],
    }));
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Main Content */}
      <div className="flex-1 p-6 bg-gray-100 overflow-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">
          Work Order Management
        </h1>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-500 mb-4"
          onClick={() => setIsModalOpen(true)}
        >
          Add Work Order Template
        </button>

        {workOrderTemplates.map((template) => (
          <div
            key={template.workOrderTemplateID}
            className="bg-white shadow-md rounded-lg p-4 mb-6"
          >
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-700">
                  {template.name}
                </h3>
                <p className="text-gray-500">{template.description}</p>
                <p className="text-sm text-gray-400">
                  {(template.workordertasks || []).length} tasks
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  className="text-blue-600 hover:underline"
                  onClick={() => {
                    setCurrentTemplate(template);
                    setIsModalOpen(true);
                  }}
                >
                  <Edit3 size={16} />
                </button>
                <button
                  className="text-red-600 hover:underline"
                  onClick={() => deleteTemplate(template.workOrderTemplateID)}
                >
                  <Trash size={16} />
                </button>
                <button
                  onClick={() => toggleTemplate(template.workOrderTemplateID)}
                >
                  {expandedTemplates[template.workOrderTemplateID] ? (
                    <ChevronDown className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-600" />
                  )}
                </button>
              </div>
            </div>

            {expandedTemplates[template.workOrderTemplateID] && (
              <>
                <DragDropContext
                  onDragEnd={(result) =>
                    handleDragEnd(result, template.workOrderTemplateID)
                  }
                >
                  <Droppable
                    droppableId={`${template.workOrderTemplateID}`}
                  >
                    {(provided) => (
                      <ul
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="space-y-4"
                      >
                        {(template.workordertasks || []).map((task, index) => (
                          <Draggable
                            key={task.id}
                            draggableId={`${task.id}`}
                            index={index}
                          >
                            {(provided) => (
                              <li
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                ref={provided.innerRef}
                                className="bg-gray-100 p-4 rounded-md shadow-sm"
                              >
                                <div className="flex justify-between items-center">
                                  <div>
                                    <h4 className="text-lg font-medium text-gray-700">
                                      {task.name}
                                    </h4>
                                    <p className="text-gray-500">
                                      {task.description}
                                    </p>
                                  </div>
                                  <div className="flex space-x-2">
                                    <button
                                      className="text-blue-600 hover:underline"
                                      onClick={() => {
                                        setTaskToEdit(task);
                                        setCurrentTemplate(template);
                                        setIsTaskModalOpen(true);
                                      }}
                                    >
                                      <Edit3 size={16} />
                                    </button>
                                    <button
                                      className="text-red-600 hover:underline"
                                      onClick={() =>
                                        deleteTask(
                                          task.id,
                                          template.workOrderTemplateID
                                        )
                                      }
                                    >
                                      <Trash size={16} />
                                    </button>
                                  </div>
                                </div>
                              </li>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </ul>
                    )}
                  </Droppable>
                </DragDropContext>

                <button
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-500 mt-4"
                  onClick={() => {
                    setCurrentTemplate(template);
                    setTaskToEdit(null);
                    setIsTaskModalOpen(true);
                  }}
                >
                  Add Task
                </button>
              </>
            )}
          </div>
        ))}

        {/* Modals */}
        {isModalOpen && (
          <WorkOrderTemplateModal
            template={currentTemplate || {}}
            onClose={() => setIsModalOpen(false)}
            onSave={saveTemplate}
          />
        )}
        {isTaskModalOpen && (
          <WorkOrderTaskModal
            task={taskToEdit || {}}
            templateId={currentTemplate?.workOrderTemplateID}
            onClose={() => setIsTaskModalOpen(false)}
            onSave={saveTask}
          />
        )}
      </div>


    </div>
  );
}

export default WorkOrderManagement;